/**
 * 메일 작성 **화면** — /mail/compose (2026-08-22).
 *
 * 여태 작성은 화면 가운데 뜨는 720px 팝업이었다. 배경이 잠기고, 뒤로가기가 안 듣고,
 * 링크로 열 수도 없었다. 주소를 주면 그 셋이 한꺼번에 풀린다.
 *
 * 무엇을 미리 채울지는 **두 갈래**로 받는다:
 *   · 라우터 state 의 `prefill` — 답장·전체답장·전달이 넘긴다(본문 인용이 길어 주소에 못 싣는다).
 *   · `?to=me` — 내게 쓰기. 새로고침해도 살아남아야 해서 주소에 적는다.
 *
 * ★ 알맹이는 ComposeModal 을 `variant="page"` 로 그대로 쓴다. 전송·임시저장·수신자 검증을
 *   두 벌로 두면 한쪽만 고쳐지는 날이 온다.
 */

import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ComposeModal, type ComposePrefill } from './ComposeModal';
import { useAuthStore } from '../../store/authStore';
import { useMailComposeStore } from '../../store/mailComposeStore';
import { tMail } from '../../lib/mail-i18n';
import './MailComposePage.css';

/** 라우터 state 로 넘어오는 것 — 답장·전달이 채워 보낸다. */
export interface MailComposeNavState {
  prefill?: ComposePrefill;
  /** 머리줄에 붙일 표식 키('mail.compose.badge_*'). 없으면 표식 없음. */
  badge?: 'self' | 'reply' | 'replyAll' | 'forward';
}

const BADGE_KEY = {
  self: 'mail.compose.badge_self',
  reply: 'mail.compose.badge_reply',
  replyAll: 'mail.compose.badge_reply_all',
  forward: 'mail.compose.badge_forward',
} as const;

export function MailComposePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const myUser = useAuthStore((s) => s.user);
  // 조직도 팝업이 담아 둔 "이 사람에게 쓰기" — 있으면 한 번 꺼내 쓴다.
  const consumePending = useMailComposeStore((s) => s.consume);

  const state = (location.state ?? null) as MailComposeNavState | null;
  const toMe = params.get('to') === 'me';

  const prefill = useMemo<ComposePrefill | null>(() => {
    if (state?.prefill) return state.prefill;
    if (toMe && myUser) {
      return { to: [{ id: String(myUser.id), name: myUser.name, email: myUser.email, position: null }] };
    }
    const pending = consumePending();
    if (pending) {
      return { to: [{ id: pending.id, name: pending.name, email: pending.email, position: pending.position ?? null }] };
    }
    return null;
    // 한 번만 만든다 — 다시 계산하면 사용자가 고친 수신자가 되돌아간다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const badge = state?.badge ?? (toMe ? 'self' : null);

  /**
   * 목록으로 돌아간다.
   *
   * ★ `navigate(-1)` 이 아니라 `/mail` 로 간다 — 이 화면에 **주소를 찍어 바로 들어올 수도**
   *   있고, 그때 뒤로 가면 앱 밖으로 나간다.
   */
  /**
   * ★ ComposeModal 은 보낸 뒤 `onSent` 와 `onClose` 를 **둘 다** 부른다(팝업 시절의 규약 —
   *   하나는 폴더를 바꾸고 하나는 창을 닫았다). 화면에서는 둘 다 이동이라, 뒤에 오는 닫기가
   *   앞의 이동을 **덮어써서** 들려 보낸 메일함이 사라진다. 보냈으면 닫기는 하지 않는다.
   */
  const sentRef = useRef(false);
  const backToList = useCallback(() => {
    if (sentRef.current) return;
    navigate('/mail');
  }, [navigate]);

  /**
   * 보냈다 / 임시 저장했다 → 목록으로 돌아가되 **그 메일이 있는 메일함**으로 간다.
   * 팝업이던 시절에는 같은 화면이라 곧장 폴더를 바꿨다 — 화면이 갈렸으니 사실을 실어 보낸다.
   */
  const afterSent = useCallback((r: { isDraft: boolean }) => {
    sentRef.current = true;
    navigate('/mail', { state: { sentFolder: r.isDraft ? 'draft' : 'sent' } });
  }, [navigate]);

  return (
    <ComposeModal
      open
      variant="page"
      pageBadge={badge ? tMail(BADGE_KEY[badge]) : null}
      prefill={prefill}
      onClose={backToList}
      onSent={afterSent}
    />
  );
}

export default MailComposePage;
